import {ReactElement} from "react";
import {useNavigate} from "react-router-dom";
import styled from "styled-components";

import {AppContext} from "@/context/AppContext";
import {
  HookFormContext,
  HookFormContextProvider,
  HookFormContextType,
} from "@/context/HookFormContext";
import {getAvancement} from "@/fonction/calcule";
import {quicksandSemiBold} from "@/GlobalStyle";
import {useButtonToDownloadContrat} from "@/hooks/useButtonToDownloadContrat/useButtonToDownoloadContrat";
import {useGetContext} from "@/hooks/useGetContext/useGetContext";
import {AppContext as AppContextType} from "@/ts/appContext";
import {Contrat} from "@/ts/contrat";

import {BlocContent} from "../BlocContent/BlocContent";
import {TableauBonus} from "../Bonus/TableauBonus/TableauBonus";
import {Button} from "../Button/Button";
import {ErrorMessage} from "../ErrorMessage/ErrorMessage";
import {TableauKpiEnCours} from "../Kpi/KpiEnCours/TableauKpiEnCours";
import {TableauAvancement} from "../TableauAvanacement/TableauAvancement";
import {Title} from "../Title/Title";

const ContentBlocTableauBord = styled.div`
  margin-top: 20px;
`;
const FooterBlocTableauBord = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
  align-content: center;
  gap: 1.5rem;
  margin: 25px 0;
`;

const NavToFournisseur = styled.a`
  display: block;
  color: ${({theme}) => theme.colors.darkSecondary};
  ${quicksandSemiBold}
  text-decoration: none;
  text-align: center;
  margin: 20px auto;
  &:hover {
    text-decoration: underline;
  }
`;

const NoContratsFound = styled.p`
  text-align: center;
  margin: 20px auto;
  ${quicksandSemiBold}
  color: ${({theme}) => theme.colors.alert};
`;

// Ce composant est utilisé pour afficher le tableau de bord des contrats
export function TableauDeBord({contrats}: {contrats: Contrat[]}): ReactElement {
  const navigate = useNavigate();
  const {API} = useGetContext<AppContextType>(AppContext, "AppContext");
  return (
    <>
      {contrats?.map(contrat => {
        const {label, icon, title, variant, handleClick} =
          useButtonToDownloadContrat({
            contrat,
            API,
          });
        const FooterTableauBord = (): ReactElement => {
          const {getValues} = useGetContext<HookFormContextType>(
            HookFormContext,
            "HookFormContext"
          );

          return (
            <FooterBlocTableauBord>
              {contrat?.campagne?.bonus.length > 0 ? (
                <div
                  style={{
                    opacity: contrat?.statut === "cloture" ? 0.6 : 1,
                    cursor:
                      contrat?.statut === "cloture" ? "not-allowed" : "pointer",
                  }}>
                  <TableauAvancement
                    avancement={getAvancement(
                      contrat,
                      contrat?.campagne?.bonus?.sort(
                        (a, b) => a.seuil_ca - b.seuil_ca
                      )[getValues("selected_bonus")?.value || 0]
                    )}
                  />
                  <NavToFournisseur
                    href={`/fournisseur/${contrat.fournisseur.fournisseur_id}`}
                    onClick={e => {
                      e.preventDefault();
                      navigate(
                        `/fournisseur/${contrat.fournisseur.fournisseur_id}`
                      );
                    }}>
                    Accéder à la page du fournisseur
                  </NavToFournisseur>
                </div>
              ) : (
                <ErrorMessage text="Aucun bonus trouvé" />
              )}

              <TableauBonus bonus={contrat?.campagne?.bonus} />
            </FooterBlocTableauBord>
          );
        };

        return (
          <BlocContent key={contrat.contrat_id}>
            <Title title={contrat?.fournisseur?.nom}>
              <Button
                label={label}
                icon={icon}
                title={title}
                variant={variant}
                onClick={handleClick}
              />
            </Title>
            {contrat?.statut == "actif" || contrat?.statut == "cloture" ? (
              <ContentBlocTableauBord>
                <TableauKpiEnCours contrat={contrat} />
              </ContentBlocTableauBord>
            ) : null}
            <HookFormContextProvider>
              <FooterTableauBord />
            </HookFormContextProvider>
          </BlocContent>
        );
      })}
      {contrats.length === 0 ? (
        <NoContratsFound>Aucun contrat trouvé</NoContratsFound>
      ) : null}
    </>
  );
}
